import { useRef, useState } from 'react';
import { AlertTriangle, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { FINISHED_IXBRL_ACCEPT, FINISHED_IXBRL_CATEGORY, OWN_IXBRL_ID } from '@/lib/ownIxbrl';
import FileUploadService from '@/services/FileUploadService';

const ACCOUNTS_ACCEPT = '.pdf,.docx,.xlsx';

/**
 * Upload control for a job that has no file yet (paid or on invoice, but the client left before the
 * file was sent). The file goes to the server as soon as it is chosen or dropped.
 */
const JobUploadCard = ({ job, onUploaded }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [failed, setFailed] = useState(null); // { file, message }
  const [dragging, setDragging] = useState(false);

  const ownIxbrl = (job.services || []).includes(OWN_IXBRL_ID);
  const category = ownIxbrl ? FINISHED_IXBRL_CATEGORY : undefined;

  const upload = async (file) => {
    const validation = FileUploadService.validateFile(file, category);
    if (!validation.isValid) {
      toast.error(validation.error);
      return;
    }
    if (validation.warning) toast.warning(validation.warning);

    setUploading(true);
    setProgress(0);
    setFailed(null);
    try {
      await FileUploadService.uploadFile(file, job.companyId, job.companyName, setProgress, {
        jobId: job.id,
        jobReference: job.jobId,
        ...(category ? { fileCategory: category } : {}),
      });
      toast.success(`${file.name} uploaded. We have emailed you a confirmation.`);
      await onUploaded();
    } catch (error) {
      setFailed({ file, message: error.message || 'Upload failed' });
      toast.error(`${file.name} did not upload: ${error.message || 'please try again'}`);
    } finally {
      setUploading(false);
    }
  };

  const handleChosen = (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (file) upload(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files[0];
    if (file && !uploading) upload(file);
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-5" data-testid="job-upload-card">
      <p className="font-medium text-amber-900 mb-1">We are waiting for your documents</p>
      <p className="text-sm text-amber-800 mb-4">
        Upload the {ownIxbrl ? 'finished iXBRL file' : 'signed accounts'} for {job.companyName}. Work starts as soon as the file arrives.
      </p>

      <div
        onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={`border border-dashed rounded-lg p-6 text-center bg-card ${dragging ? 'border-primary' : 'border-amber-300'}`}
        data-testid="job-upload-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept={ownIxbrl ? FINISHED_IXBRL_ACCEPT : ACCOUNTS_ACCEPT}
          onChange={handleChosen}
          className="hidden"
          data-testid="job-upload-input"
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            <p className="text-sm">Uploading… {progress}%</p>
            <p className="text-xs text-muted-foreground">Keep this page open until it finishes.</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="w-6 h-6 text-muted-foreground" />
            <p className="text-sm">Drop your file here, or</p>
            <Button onClick={() => inputRef.current?.click()} data-testid="job-upload-browse">
              Choose file
            </Button>
            <p className="text-xs text-muted-foreground">
              {ownIxbrl ? 'XHTML (.xhtml, .html, .htm), up to 25 MB' : 'PDF, Word (.docx) or Excel (.xlsx), up to 100 MB'}
            </p>
          </div>
        )}
      </div>

      {failed && (
        <div className="flex items-start gap-2 mt-3 text-sm text-red-700" role="alert" data-testid="job-upload-error">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p>{failed.file.name} did not upload: {failed.message}</p>
            <button type="button" className="underline" onClick={() => upload(failed.file)} data-testid="job-upload-retry">
              Try again
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobUploadCard;
